(() => {
  const SELECTORS = {
    page: '[data-finance-page]',
    form: '[data-finance-form]',
    step: '[data-form-step]',
    field: '[data-finance-field]',
    fieldError: '[data-field-error]',
    errorSummary: '[data-error-summary]',
    nextButton: '[data-next-step]',
    previousButton: '[data-previous-step]',
    progress: '[data-form-progress]',
    progressFill: '[data-form-progress-fill]',
    progressLabel: '[data-form-step-label]',
    reviewField: '[data-review-field]',
    submitButton: '[data-submit-button]',
    submitLabel: '[data-submit-label]',
    submitLoading: '[data-submit-loading]',
    status: '[data-form-status]',
    success: '[data-form-success]',
    intro: '[data-form-intro]',
    securityNote: '[data-form-security-note]',
    honeypot: '[data-honeypot]'
  };

  const STEP_ONE_FIELDS = [
    'fullName',
    'emailAddress',
    'phoneNumber',
    'businessName',
    'businessCategory',
    'financeAmount'
  ];

  const STEP_TWO_FIELDS = ['abnOrAcn', 'acknowledgement'];

  const normalizedDigits = (value) => String(value || '').replace(/\D/g, '');

  const validators = {
    fullName: (field) => field.value.trim().length >= 2,
    emailAddress: (field) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim()),
    phoneNumber: (field) => {
      const digits = normalizedDigits(field.value);
      return digits.length >= 8 && digits.length <= 15;
    },
    businessName: (field) => field.value.trim().length >= 2,
    businessCategory: (field) => field.value !== '',
    financeAmount: (field) => field.value !== '',
    abnOrAcn: (field) => {
      const digits = normalizedDigits(field.value);
      return digits.length === 9 || digits.length === 11;
    },
    acknowledgement: (field) => field.checked
  };

  function emitAnalytics(eventName, payload) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
  }

  function initializeFinancePage(page) {
    if (page.hasAttribute('data-finance-initialized')) return;

    const form = page.querySelector(SELECTORS.form);
    if (!form) return;

    page.setAttribute('data-finance-initialized', 'true');

    const fields = new Map();
    form.querySelectorAll(SELECTORS.field).forEach((field) => {
      fields.set(field.dataset.financeField, field);
    });

    const errorSummary = form.querySelector(SELECTORS.errorSummary);
    const nextButton = form.querySelector(SELECTORS.nextButton);
    const previousButton = form.querySelector(SELECTORS.previousButton);
    const progress = page.querySelector(SELECTORS.progress);
    const progressFill = page.querySelector(SELECTORS.progressFill);
    const progressLabel = page.querySelector(SELECTORS.progressLabel);
    const submitButton = form.querySelector(SELECTORS.submitButton);
    const submitLabel = form.querySelector(SELECTORS.submitLabel);
    const submitLoading = form.querySelector(SELECTORS.submitLoading);
    const status = form.querySelector(SELECTORS.status);
    const success = page.querySelector(SELECTORS.success);
    const intro = page.querySelector(SELECTORS.intro);
    const securityNote = page.querySelector(SELECTORS.securityNote);
    const honeypot = form.querySelector(SELECTORS.honeypot);
    const steps = Array.from(form.querySelectorAll(SELECTORS.step));
    let currentStep = 1;
    let isSubmitting = false;

    function getFieldError(name) {
      return form.querySelector(`${SELECTORS.fieldError}[data-field-error="${name}"]`);
    }

    function setFieldValidity(name, isValid) {
      const field = fields.get(name);
      const error = getFieldError(name);
      if (!field) return;

      if (isValid) {
        field.removeAttribute('aria-invalid');
      } else {
        field.setAttribute('aria-invalid', 'true');
      }

      if (error) error.hidden = isValid;
    }

    function validateFields(fieldNames) {
      let firstInvalidField = null;

      fieldNames.forEach((name) => {
        const field = fields.get(name);
        const validator = validators[name];
        const isValid = Boolean(field && validator && validator(field));
        setFieldValidity(name, isValid);
        if (!isValid && !firstInvalidField) firstInvalidField = field;
      });

      if (firstInvalidField) {
        errorSummary.hidden = false;
        firstInvalidField.focus();
        return false;
      }

      errorSummary.hidden = true;
      return true;
    }

    function selectedText(field) {
      if (!(field instanceof HTMLSelectElement)) return field.value.trim();
      return field.options[field.selectedIndex]?.text || '';
    }

    function updateReview() {
      form.querySelectorAll(SELECTORS.reviewField).forEach((reviewField) => {
        const field = fields.get(reviewField.dataset.reviewField);
        reviewField.textContent = field ? selectedText(field) : '';
      });
    }

    function showStep(stepNumber, focusTarget) {
      currentStep = stepNumber;
      steps.forEach((step) => {
        step.hidden = Number(step.dataset.formStep) !== stepNumber;
      });

      progress.setAttribute('aria-valuenow', String(stepNumber));
      progress.setAttribute('aria-valuetext', `Step ${stepNumber} of 2`);
      progressLabel.textContent = `Step ${stepNumber} of 2`;
      progressFill.style.width = stepNumber === 1 ? '33%' : '100%';
      errorSummary.hidden = true;
      status.hidden = true;

      if (focusTarget) focusTarget.focus();
    }

    function goToStepTwo() {
      if (!validateFields(STEP_ONE_FIELDS)) return;

      updateReview();
      const stepTwoHeading = form.querySelector('[data-form-step="2"] h3');
      showStep(2, stepTwoHeading);
      emitAnalytics('form_step_progress', {
        step_number: 1,
        step_name: 'basic_info',
        status: 'next_clicked'
      });
    }

    function goToStepOne() {
      const firstField = fields.get('fullName');
      showStep(1, firstField);
      emitAnalytics('form_step_progress', {
        step_number: 2,
        step_name: 'business_details',
        status: 'prev_clicked'
      });
    }

    function setSubmitting(submitting) {
      isSubmitting = submitting;
      submitButton.disabled = submitting;
      previousButton.disabled = submitting;
      submitButton.setAttribute('aria-busy', String(submitting));
      submitLabel.hidden = submitting;
      submitLoading.hidden = !submitting;
    }

    function leadPayload() {
      return {
        fullName: fields.get('fullName').value.trim(),
        emailAddress: fields.get('emailAddress').value.trim(),
        businessName: fields.get('businessName').value.trim(),
        abnOrAcn: fields.get('abnOrAcn').value.trim(),
        phoneNumber: fields.get('phoneNumber').value.trim(),
        businessCategory: fields.get('businessCategory').value,
        financeAmount: fields.get('financeAmount').value
      };
    }

    async function submitLead() {
      if (isSubmitting || !validateFields(STEP_TWO_FIELDS)) return;

      if (honeypot?.value) {
        showSuccess();
        return;
      }

      setSubmitting(true);
      status.hidden = true;
      const payload = leadPayload();

      try {
        try {
          const attributionRequest = window.FlexikitchLeadAttribution?.submitLead?.({
            email: payload.emailAddress,
            name: payload.fullName,
            phone: payload.phoneNumber,
            company: payload.businessName,
            lead_source: 'Apply for finance',
            raw_payload: {
              form_name: 'apply-for-finance-refresh',
              zapier_payload: payload
            }
          });
          let attributionTimeout;
          const attribution = await Promise.race([
            Promise.resolve(attributionRequest),
            new Promise((resolve) => {
              attributionTimeout = window.setTimeout(() => resolve(null), 3000);
            })
          ]);
          window.clearTimeout(attributionTimeout);

          if (attribution?.submissionKey) {
            payload.lead_attribution_submission_key = attribution.submissionKey;
          }
        } catch (_attributionError) {
          void _attributionError;
        }

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 20000);
        let response;

        try {
          response = await window.fetch(form.dataset.endpoint, {
            method: 'POST',
            body: new URLSearchParams(payload),
            signal: controller.signal
          });
        } finally {
          window.clearTimeout(timeout);
        }

        if (!response.ok) throw new Error('Submission failed');

        emitAnalytics('form_submission', {
          form_name: 'business_financing_form',
          status: 'submitted'
        });
        showSuccess();
      } catch (_submissionError) {
        status.textContent = form.dataset.errorMessage;
        status.hidden = false;
        status.focus?.();
        setSubmitting(false);
      }
    }

    function showSuccess() {
      form.hidden = true;
      progress.hidden = true;
      if (intro) intro.hidden = true;
      if (securityNote) securityNote.hidden = true;
      success.hidden = false;
      success.focus();
    }

    fields.forEach((field, name) => {
      const clearError = () => {
        if (!field.hasAttribute('aria-invalid')) return;
        const validator = validators[name];
        if (validator?.(field)) setFieldValidity(name, true);
      };
      field.addEventListener('input', clearError);
      field.addEventListener('change', clearError);
    });

    nextButton.addEventListener('click', goToStepTwo);
    previousButton.addEventListener('click', goToStepOne);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (currentStep === 1) {
        goToStepTwo();
      } else {
        submitLead();
      }
    });
  }

  function initializeAllFinancePages(root = document) {
    root.querySelectorAll(SELECTORS.page).forEach(initializeFinancePage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllFinancePages());
  } else {
    initializeAllFinancePages();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAllFinancePages(event.target));
})();
